'use client'
import { PrimitiveIcon } from '@gluestack-ui/core/icon/creator'
import { createInput } from '@gluestack-ui/core/input/creator'
import type { VariantProps } from '@gluestack-ui/utils/nativewind-utils'
import { tva } from '@gluestack-ui/utils/nativewind-utils'
import { useStyleContext, withStyleContext } from '@gluestack-ui/utils/nativewind-utils'
import { cssInterop } from 'nativewind'
import React from 'react'
import { Pressable, TextInput, View } from 'react-native'

import { UIIcon } from '../icon/UIIcon'

const SCOPE = 'INPUT'

const UIInput = createInput({
    Root: withStyleContext(View, SCOPE),
    Icon: UIIcon,
    Slot: Pressable,
    Input: TextInput,
})

cssInterop(PrimitiveIcon, {
    className: {
        target: 'style',
        nativeStyleToProp: {
            height: true,
            width: true,
            fill: true,
            color: 'classNameColor',
            stroke: true,
        },
    },
})

// AOS redesign (AOS-600): default border = border/subtle, hover = border/strong,
// focus = border/focus (the action blue #3459C6 — NOT primary-700 which is the
// dark navy aos-primary.surface), and the field sits on the input bg #111114.
// Structural scaffolding only. All presentation (bg, border, state colours,
// size, text) lives in `components/input` — the single source of truth, matching
// the Figma Input states. The gluestack interaction wiring (data-[hover|focus|
// invalid|disabled] on the root) is preserved; no colours are baked here.
const inputStyle = tva({
    base: 'flex-row overflow-hidden content-center items-center',
    variants: {
        size: { xl: '', lg: '', md: '', sm: '' },
        variant: { underlined: '', outline: '', rounded: '' },
    },
})

const inputIconStyle = tva({
    base: 'justify-center items-center fill-none',
    parentVariants: {
        size: { '2xs': '', 'xs': '', 'sm': '', 'md': '', 'lg': '', 'xl': '' },
    },
})

const inputSlotStyle = tva({
    base: 'justify-center items-center web:disabled:cursor-not-allowed',
})

const inputFieldStyle = tva({
    base: 'flex-1 h-full web:outline-0 web:outline-none web:cursor-text web:data-[disabled=true]:cursor-not-allowed',
    parentVariants: {
        variant: { underlined: '', outline: '', rounded: '' },
        size: {
            '2xs': '',
            'xs': '',
            'sm': '',
            'md': '',
            'lg': '',
            'xl': '',
            '2xl': '',
            '3xl': '',
            '4xl': '',
            '5xl': '',
            '6xl': '',
        },
    },
})

type IInputProps = React.ComponentProps<typeof UIInput> &
    VariantProps<typeof inputStyle> & { className?: string }
const Input = React.forwardRef<React.ComponentRef<typeof UIInput>, IInputProps>(function Input(
    { className, variant = 'outline', size = 'md', ...props },
    ref,
) {
    return (
        <UIInput
            ref={ref}
            {...props}
            className={inputStyle({ variant, size, class: className })}
            context={{ variant, size }}
        />
    )
})

type IInputIconProps = React.ComponentProps<typeof UIInput.Icon> &
    VariantProps<typeof inputIconStyle> & {
        className?: string
        height?: number
        width?: number
    }

const InputIcon = React.forwardRef<React.ComponentRef<typeof UIInput.Icon>, IInputIconProps>(
    function InputIcon({ className, size, ...props }, ref) {
        const { size: parentSize } = useStyleContext(SCOPE)

        if (typeof size === 'number') {
            return (
                <UIInput.Icon
                    ref={ref}
                    {...props}
                    className={inputIconStyle({ class: className })}
                    size={size}
                />
            )
        } else if (
            (props.height !== undefined || props.width !== undefined) &&
            size === undefined
        ) {
            return (
                <UIInput.Icon
                    ref={ref}
                    {...props}
                    className={inputIconStyle({ class: className })}
                />
            )
        }
        return (
            <UIInput.Icon
                ref={ref}
                {...props}
                className={inputIconStyle({
                    parentVariants: {
                        size: parentSize,
                    },
                    class: className,
                })}
            />
        )
    },
)

type IInputSlotProps = React.ComponentProps<typeof UIInput.Slot> &
    VariantProps<typeof inputSlotStyle> & { className?: string }

const InputSlot = React.forwardRef<React.ComponentRef<typeof UIInput.Slot>, IInputSlotProps>(
    function InputSlot({ className, ...props }, ref) {
        return (
            <UIInput.Slot
                ref={ref}
                {...props}
                className={inputSlotStyle({
                    class: className,
                })}
            />
        )
    },
)

type IInputFieldProps = React.ComponentProps<typeof UIInput.Input> &
    VariantProps<typeof inputFieldStyle> & { className?: string }

const InputField = React.forwardRef<React.ComponentRef<typeof UIInput.Input>, IInputFieldProps>(
    // AOS layouts use fixed Figma pixel tokens, not Dynamic Type-scaled text —
    // default it off, callers can still opt back in explicitly.
    function InputField({ className, allowFontScaling = false, ...props }, ref) {
        const { variant: parentVariant, size: parentSize } = useStyleContext(SCOPE)

        return (
            <UIInput.Input
                ref={ref}
                allowFontScaling={allowFontScaling}
                {...props}
                className={inputFieldStyle({
                    parentVariants: {
                        variant: parentVariant,
                        size: parentSize,
                    },
                    class: className,
                })}
            />
        )
    },
)

Input.displayName = 'Input'
InputIcon.displayName = 'InputIcon'
InputSlot.displayName = 'InputSlot'
InputField.displayName = 'InputField'

export { Input, InputField, InputIcon, InputSlot }
